import React, { useState, useEffect, Fragment } from "react";
import { Table, Button, Modal, Input, message, Tag } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import AddDeviceForm from "../tools/Form/AddDevice";
import EditDeviceForm from "../tools/Form/EditDevice";
import dayjs from "dayjs";
import * as XLSX from "xlsx"; // Thư viện xuất file Excel
import { hasAnyPermission, useAuth } from "../../services/auth";
import { deleteWarehouseDevice, getWarehouseDevices, resetWarehouseDevice } from "../../services/warehouse-devices";

const { confirm } = Modal;

// Component UserGrid
export const UserGrid = ({ users }) => {
  const columns = [
    { title: "Mã người dùng", dataIndex: "user_id", key: "user_id" },
    { title: "Số điện thoại", dataIndex: "phone", key: "phone" },
    { title: "Tên người dùng", dataIndex: "name", key: "name" },
    {
      title: "Ngày bắt đầu",
      dataIndex: "start_date",
      key: "start_date",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
    },
    { title: "Vai trò", dataIndex: "role", key: "role" },
    {
      title: "Ngày hết hạn",
      dataIndex: "expire_date",
      key: "expire_date",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={users}
      rowKey={(record) => record.user_id}
      pagination={false}
      size="small"
      bordered
    />
  );
};

// Component MasterDetail chính
export default function MasterDetail() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [rowToEdit, setRowToEdit] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [user] = useAuth();
  const canEditWarehouse = hasAnyPermission(user, ["warehouse_edit", "warehouse_manage"]);
  const canManageWarehouse = hasAnyPermission(user, ["warehouse_manage"]);

  useEffect(() => {
    onInitData(1, pagination.pageSize, debouncedSearchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const onInitData = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    search = debouncedSearchText
  ) => {
    try {
      setIsLoading(true);
      const res = await getWarehouseDevices({ page, pageSize, search });
      setRows(res.data);
      setExpandedRowKeys([]);
      setPagination({
        current: page,
        pageSize,
        total: res.total || res.data.length,
      });
    } catch (e) {
      console.error(e);
      message.error("Không thể tải dữ liệu.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetClick = (row) => {
    if (!row.users || row.users.length === 0) {
      message.warning("Thiết bị chưa có người sử dụng.");
    } else {
      confirm({
        title: `Reset ${row.qr_string}`,
        icon: <ExclamationCircleOutlined />,
        content:
          "Thao tác này sẽ hủy liên kết dữ liệu. Bạn có chắc chắn không?",
        onOk: async () => {
          try {
            setIsLoading(true);
            const success = await resetWarehouseDevice(row.id);
            if (success) {
              onInitData();
              message.success("Thiết bị đã được reset thành công!");
            } else {
              message.error("Có lỗi xảy ra, không thể reset thiết bị!");
            }
          } catch (error) {
            console.error("Lỗi khi reset thiết bị:", error);
            message.error("Có lỗi xảy ra khi kết nối với máy chủ!");
          } finally {
            setIsLoading(false);
          }
        },
      });
    }
  };

  const handleDeleteClick = (row) => {
    if (!!row.users.length) {
      message.warning(
        "Thiết bị đang có người sử dụng. Vui lòng reset trước khi xóa"
      );
    } else {
      confirm({
        title: `Xóa ${row.qr_string}`,
        icon: <ExclamationCircleOutlined />,
        content:
          "Bạn có chắc chắn muốn xóa thiết bị này? Thao tác này không thể hoàn tác.",
        onOk: async () => {
          try {
            setIsLoading(true);
            await deleteWarehouseDevice(row.id);
            onInitData();
            message.success("Thiết bị đã được xóa thành công!");
          } catch (error) {
            console.error("Lỗi khi xóa thiết bị:", error);
            message.error("Có lỗi xảy ra khi kết nối với máy chủ!");
          } finally {
            setIsLoading(false);
          }
        },
      });
    }
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleAddClick = () => {
    setOpenAddDialog(true);
  };

  const handleEditClick = (row) => {
    setRowToEdit(row);
    setOpenEditDialog(true);
  };

  const statusMap = {
    0: { label: "Khởi tạo", color: "blue" },
    1: { label: "Kiểm tra", color: "gold" },
    2: { label: "Tồn kho", color: "cyan" },
    3: { label: "Kích hoạt", color: "green" },
    4: { label: "Hoàn trả", color: "red" },
  };

  const columns = [
    {
      title: "STT",
      key: "index",
      width: 60,
      align: "center",
      render: (text, record, index) => (pagination.current - 1) * pagination.pageSize + index + 1, // Hiển thị số thứ tự
    },
    {
      title: "Mã thiết bị",
      dataIndex: "qr_string",
      sorter: (a, b) => a.qr_string.localeCompare(b.qr_string),
      sortDirections: ["ascend", "descend"],
      width: 150,
    },
    {
      title: "Loại",
      dataIndex: "device_type",
      filters: [
        { text: "S3", value: 0 },
        { text: "S7", value: 1 },
        { text: "S4", value: 2 },
        { text: "LN", value: 3 },
        { text: "BLE", value: 4 },
        { text: "GW", value: 5 },
      ],
      onFilter: (value, record) => record.device_type === value,
      render: (value) => {
        const typeMap = {
          0: "S3",
          1: "S7",
          2: "S4",
          3: "LN",
          4: "BLE",
          5: "GW",
        };
        return typeMap[value] || value;
      },
      width: 100,
    },
    {
      title: "Lô nhập",
      dataIndex: "lot",
      filters: [...new Set(rows.map((row) => row.lot))].map((lot) => ({
        text: lot ?? "Không xác định",
        value: lot,
      })),
      onFilter: (value, record) => record.lot === value,
      width: 100,
    },
    {
      title: "Nhóm",
      dataIndex: "group",
      filters: [...new Set(rows.map((row) => row.group))].map((group) => ({
        text: group ?? "Không xác định",
        value: group,
      })),
      onFilter: (value, record) => record.group === value,
      width: 100,
    },
    {
      title: "Ngày nhập",
      dataIndex: "imported_at",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
      width: 120,
    },
    {
      title: "Ngày kiểm tra",
      dataIndex: "tested_at",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
      width: 120,
    },
    {
      title: "Người nhập",
      dataIndex: "creator",
      width: 120,
    },
    {
      title: "Ngày thêm",
      dataIndex: "init_date",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
      width: 160,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      filters: [
        { text: "Khởi tạo", value: 0 },
        { text: "Kiểm tra", value: 1 },
        { text: "Tồn kho", value: 2 },
        { text: "Kích hoạt", value: 3 },
        { text: "Hoàn trả", value: 4 },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const { label, color } = statusMap[status] || {
          label: "Không xác định",
          color: "default",
        };
        return <Tag color={color}>{label}</Tag>;
      },
      width: 150,
    },
    { title: "Ghi chú", dataIndex: "note", width: 120 },
    {
      title: "Tác vụ",
      render: (_, row) => (
        <>
          {!!row.users.length ? (
            <Fragment>
              <Button
                variant="filled"
                color="default"
                icon={<ReloadOutlined />}
                onClick={() => handleResetClick(row)}
                style={{ marginRight: 8 }}
                disabled={!canEditWarehouse}
              />
            </Fragment>
          ) : (
            <Button
              variant="filled"
              color="danger"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteClick(row)}
              style={{ marginRight: 8 }}
              disabled={!canManageWarehouse}
            />
          )}

          <Button
            variant="filled"
            color="primary"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(row)}
            disabled={!canEditWarehouse}
          />
        </>
      ),
      width: 180,
      align: "center",
      fixed: "right",
    },
  ];

  const handleExport = () => {
    const formattedData = rows.map((row, index) => ({
      STT: index + 1,
      "Mã thiết bị": row.qr_string,
      "Loại thiết bị": ["S3", "S7", "S4", "LN", "BLE", "GW"][row.device_type],
      "Lô nhập": row.lot,
      "Ngày nhập": row.imported_at
        ? dayjs(row.imported_at).format("DD/MM/YYYY")
        : "",
      "Ngày kiểm tra": row.tested_at
        ? dayjs(row.tested_at).format("DD/MM/YYYY")
        : "",
      "Trạng thái": [
        "Khởi tạo",
        "Kiểm tra",
        "Tồn kho",
        "Kích hoạt",
        "Hoàn trả",
      ][row.status],
      Nhóm: row.group,
      "Ghi chú": row.note,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Devices");

    // Xuất file Excel
    XLSX.writeFile(workbook, "DanhSachThietBi.xlsx");
  };

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Input.Search
          placeholder="Tìm kiếm mã thiết bị"
          allowClear
          onChange={handleSearch}
          style={{ width: 300 }}
        />
        <div>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            style={{ marginRight: 8 }}
          >
            Xuất Excel
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddClick}
            disabled={!canEditWarehouse}
          >
            Thêm thiết bị
          </Button>
        </div>
      </div>
      <Table
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        rowKey={(row) => row.id}
        expandable={{
          expandedRowRender: (record) =>
            record.users?.length ? (
              <UserGrid users={record.users} />
            ) : (
              "Không có dữ liệu người dùng."
            ),
          rowExpandable: (record) => record.users?.length > 0,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(
              expanded
                ? [...expandedRowKeys, record.id]
                : expandedRowKeys.filter((id) => id !== record.id)
            );
          },
        }}
        bordered
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
        }}
        onChange={(nextPagination) => {
          onInitData(nextPagination.current, nextPagination.pageSize, debouncedSearchText);
        }}
        size="small"
        scroll={{
          x: 1260,
          scrollToFirstRowOnChange: true,
          y: `calc(-300px + 100vh)`,
        }}
      />

      <AddDeviceForm
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onAddComplete={onInitData}
      />

      <EditDeviceForm
        open={openEditDialog}
        deviceData={rowToEdit}
        onClose={() => setOpenEditDialog(false)}
        onEditComplete={onInitData}
      />
    </div>
  );
}
